/**
 * ClickReel SDK - Video Player Overlay
 *
 * Full-screen modal on mobile, PiP-style on desktop
 * Lazy loads video only on interaction
 */

import { VideoConfig, VideoProgressData, BubblePosition, StylingConfig, ProductCardConfig, SocialProofConfig } from './types';
import { getShadowContainer } from './shadow-dom';
import { ProductCard } from './product-card';
import { createUnmuteOverlay, getConnectionQuality, createBufferingOverlay, createTapToLoadPlaceholder, injectUtilityStyles, isMobile } from './video-utils';
import { buildCtaUrl } from './analytics-bridge';

// =============================================================================
// Video Player Component
// =============================================================================

export interface VideoPlayerOptions {
  video: VideoConfig;
  position: BubblePosition;
  styling: StylingConfig;
  onPlay?: () => void;
  onPause?: () => void;
  onProgress?: (data: VideoProgressData) => void;
  onComplete?: () => void;
  onClose?: () => void;
  onCtaClick?: () => void;
  muteOnOpen?: boolean;
  productCard?: ProductCardConfig;
  socialProof?: SocialProofConfig;
  storyMode?: boolean;
  storyIndex?: number;
  storyTotal?: number;
  onStoryAdvance?: (direction: 'next' | 'prev') => void;
  showBranding?: boolean;
  campaignName?: string;
  sessionId?: string;
}

/**
 * Video player overlay component
 */
export class VideoPlayer {
  private overlay: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private options: VideoPlayerOptions;
  private isOpen = false;
  private lastQuartile = 0;
  private progressInterval: number | null = null;
  private productCardInstance: ProductCard | null = null;
  private bufferingOverlay: HTMLElement | null = null;
  private closing = false;
  private destroyed = false;
  // Cached DOM element references for progress updates
  private progressFillEl: HTMLElement | null = null;
  private timeDisplayEl: HTMLElement | null = null;
  private storyFillEl: HTMLElement | null = null;
  private rafId: number | null = null;

  constructor(options: VideoPlayerOptions) {
    this.options = options;
  }

  /**
   * Open the video player
   */
  open(): void {
    if (this.isOpen) return;

    const shadow = getShadowContainer();
    if (!shadow) {
      throw new Error('Shadow DOM not initialized');
    }

    this.render(shadow);
    this.isOpen = true;

    // Start playing after render
    requestAnimationFrame(() => {
      this.playVideo();
    });
  }

  /**
   * Close the video player
   */
  close(): void {
    if (!this.isOpen || !this.overlay) return;
    this.closing = true;

    this.pauseVideo();
    this.stopProgressTracking();

    // Animate out
    this.overlay.classList.add('closing');

    setTimeout(() => {
      this.destroy();
      this.options.onClose?.();
    }, 200);
  }

  /**
   * Toggle play/pause
   */
  togglePlay(): void {
    if (!this.videoElement) return;

    if (this.videoElement.paused) {
      this.playVideo();
    } else {
      this.pauseVideo();
    }
  }

  /**
   * Check if player is currently open
   */
  get isVisible(): boolean {
    return this.isOpen;
  }

  /**
   * Destroy the player and clean up
   */
  destroy(): void {
    this.destroyed = true;
    this.closing = true;
    this.stopProgressTracking();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.productCardInstance?.destroy();
    this.productCardInstance = null;
    this.bufferingOverlay = null;
    this.progressFillEl = null;
    this.timeDisplayEl = null;
    this.storyFillEl = null;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.container = null;
      this.videoElement = null;
    }
    this.isOpen = false;
    this.lastQuartile = 0;
  }

  // ===========================================================================
  // Private Methods - Rendering
  // ===========================================================================

  private render(shadow: ShadowRoot): void {
    const mobile = isMobile();

    // Create overlay backdrop
    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-video-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'Video player');

    // Click outside to close (on backdrop)
    // 300ms debounce prevents iOS Shadow DOM touch misfire on open
    const openTime = Date.now();
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay && Date.now() - openTime > 300) {
        this.close();
      }
    });

    // Create container
    this.container = document.createElement('div');
    this.container.className = `clickreel-video-container ${mobile ? 'mobile' : 'desktop'}`;

    // Apply position-based styles for desktop PiP
    if (!mobile) {
      this.applyPiPStyles();
    }

    // Build player content
    this.container.appendChild(this.createCloseButton());

    const videoWrapper = this.createVideoContainer();

    // Product card
    if (this.options.productCard?.enabled) {
      this.productCardInstance = new ProductCard(
        this.options.productCard,
        this.options.styling.primaryColor,
      );
      this.productCardInstance.render(videoWrapper);
    }

    // Social proof
    if (this.options.socialProof?.enabled) {
      this.createSocialProof(videoWrapper);
    }

    this.container.appendChild(videoWrapper);

    if (this.options.showBranding) {
      const watermark = document.createElement('a');
      watermark.href = 'https://clickreel.io?utm_source=widget&utm_medium=referral&utm_campaign=powered_by';
      watermark.target = '_blank';
      watermark.rel = 'noopener';
      watermark.textContent = 'Powered by ClickReel.io';
      watermark.className = 'clickreel-player-watermark';
      videoWrapper.appendChild(watermark);
    }

    if (this.options.video.ctaText && this.options.video.ctaUrl) {
      this.container.appendChild(this.createCTAButton());
    }

    this.overlay.appendChild(this.container);

    // Inject styles
    this.injectStyles(shadow);

    shadow.appendChild(this.overlay);

    // Cache DOM element references for progress updates (avoid per-frame queries)
    this.progressFillEl = this.container.querySelector('.clickreel-progress-fill') as HTMLElement;
    this.timeDisplayEl = this.container.querySelector('.clickreel-time-display') as HTMLElement;
    if (this.options.storyMode) {
      this.storyFillEl = this.container.querySelector('.clickreel-story-segment.active .clickreel-story-fill') as HTMLElement;
    }

    // Handle escape key
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  private createCloseButton(): HTMLElement {
    const button = document.createElement('button');
    button.className = 'clickreel-close-btn';
    button.setAttribute('aria-label', 'Close video');
    button.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.3 5.71a.996.996 0 00-1.41 0L12 10.59 7.11 5.7A.996.996 0 105.7 7.11L10.59 12 5.7 16.89a.996.996 0 101.41 1.41L12 13.41l4.89 4.89a.996.996 0 101.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>
      </svg>
    `;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    return button;
  }

  private createVideoContainer(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'clickreel-video-wrapper';

    // Apply aspect ratio based on orientation
    const { orientation } = this.options.video;
    wrapper.setAttribute('data-orientation', orientation);

    // Adaptive quality: check connection
    const connectionQuality = getConnectionQuality();

    // Create video element (lazy - loads only when opened)
    this.videoElement = document.createElement('video');
    this.videoElement.className = 'clickreel-video';
    this.videoElement.src = this.options.video.url;
    this.videoElement.poster = this.options.video.thumbnailUrl || '';
    this.videoElement.playsInline = true;

    // Adaptive quality: adjust preload based on connection
    if (connectionQuality === 'slow') {
      this.videoElement.preload = 'none';
    } else {
      this.videoElement.preload = 'metadata';
    }

    // Attach video event listeners
    this.attachVideoEvents();

    // Adaptive quality: buffering overlay for medium connections
    if (connectionQuality === 'medium') {
      this.videoElement.addEventListener('waiting', () => {
        if (!this.bufferingOverlay && wrapper) {
          this.bufferingOverlay = createBufferingOverlay(wrapper);
        }
        if (this.bufferingOverlay) {
          this.bufferingOverlay.classList.remove('hidden');
        }
      });
      this.videoElement.addEventListener('playing', () => {
        if (this.bufferingOverlay) {
          this.bufferingOverlay.classList.add('hidden');
        }
      });
    }

    // Create custom controls
    const controls = this.createVideoControls();

    wrapper.appendChild(this.videoElement);
    wrapper.appendChild(controls);

    // Adaptive quality: tap-to-load placeholder for slow connections
    if (connectionQuality === 'slow') {
      const placeholder = createTapToLoadPlaceholder(() => {
        this.playVideo();
      }, this.options.video.thumbnailUrl);
      wrapper.appendChild(placeholder);
    }

    // Story mode UI
    if (this.options.storyMode) {
      this.createStoryUI(wrapper);
    }

    return wrapper;
  }

  private createVideoControls(): HTMLElement {
    const controls = document.createElement('div');
    controls.className = 'clickreel-video-controls';

    // Play/Pause button
    const playBtn = document.createElement('button');
    playBtn.className = 'clickreel-play-btn';
    playBtn.innerHTML = this.getPlayIcon();
    playBtn.addEventListener('click', () => this.togglePlay());

    // Progress bar
    const progressContainer = document.createElement('div');
    progressContainer.className = 'clickreel-progress-container';

    const progressBar = document.createElement('div');
    progressBar.className = 'clickreel-progress-bar';

    const progressFill = document.createElement('div');
    progressFill.className = 'clickreel-progress-fill';

    progressBar.appendChild(progressFill);
    progressContainer.appendChild(progressBar);

    // Click on progress bar to seek
    progressContainer.addEventListener('click', (e) => {
      if (!this.videoElement) return;
      const rect = progressBar.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      this.videoElement.currentTime = percent * this.videoElement.duration;
    });

    // Time display
    const timeDisplay = document.createElement('span');
    timeDisplay.className = 'clickreel-time-display';
    timeDisplay.textContent = '0:00 / 0:00';

    // Volume/Mute button
    const muteBtn = document.createElement('button');
    muteBtn.className = 'clickreel-mute-btn';
    muteBtn.innerHTML = this.getVolumeIcon();
    muteBtn.addEventListener('click', () => {
      if (!this.videoElement) return;
      this.videoElement.muted = !this.videoElement.muted;
      muteBtn.innerHTML = this.videoElement.muted ? this.getMuteIcon() : this.getVolumeIcon();
    });

    controls.appendChild(playBtn);
    controls.appendChild(progressContainer);
    controls.appendChild(timeDisplay);
    controls.appendChild(muteBtn);

    return controls;
  }

  private createCTAButton(): HTMLElement {
    const { video, styling } = this.options;

    const cta = document.createElement('a');
    cta.className = 'clickreel-cta-btn';
    cta.href = buildCtaUrl(video.ctaUrl, {
      campaignName: this.options.campaignName,
      videoId: video.id,
      sessionId: this.options.sessionId,
    });
    cta.target = '_blank';
    cta.rel = 'noopener noreferrer';
    cta.textContent = video.ctaText || 'Learn More';
    cta.style.backgroundColor = styling.primaryColor || '#6366f1';

    cta.addEventListener('click', () => {
      this.options.onCtaClick?.();
    });

    return cta;
  }

  private applyPiPStyles(): void {
    if (!this.container) return;

    const { position } = this.options;
    const margin = '24px';

    let positionStyle = '';
    switch (position) {
      case 'bottom-right':
        positionStyle = `bottom: ${margin}; right: ${margin};`;
        break;
      case 'bottom-left':
        positionStyle = `bottom: ${margin}; left: ${margin};`;
        break;
      case 'top-right':
        positionStyle = `top: ${margin}; right: ${margin};`;
        break;
      case 'top-left':
        positionStyle = `top: ${margin}; left: ${margin};`;
        break;
    }

    this.container.style.cssText = positionStyle;
  }

  // ===========================================================================
  // Private Methods - Video Controls
  // ===========================================================================

  private attachVideoEvents(): void {
    if (!this.videoElement) return;

    this.videoElement.addEventListener('play', () => {
      this.options.onPlay?.();
      this.updatePlayButton(true);
      this.startProgressTracking();
    });

    this.videoElement.addEventListener('pause', () => {
      this.options.onPause?.();
      this.updatePlayButton(false);
    });

    this.videoElement.addEventListener('playing', () => {
      if (this.videoElement?.muted) {
        const wrapper = this.videoElement.parentElement;
        if (wrapper) {
          createUnmuteOverlay(() => {
            if (this.videoElement) {
              this.videoElement.muted = false;
              this.updateMuteButton(false);
            }
          }, wrapper);
        }
      }
    }, { once: true });

    this.videoElement.addEventListener('ended', () => {
      if (this.destroyed || this.closing) return;
      this.options.onComplete?.();
      this.updatePlayButton(false);
      this.stopProgressTracking();
      this.emitProgress(100);
      if (this.options.storyMode && !this.closing && !this.destroyed) {
        this.options.onStoryAdvance?.('next');
      }
    });

    this.videoElement.addEventListener('timeupdate', () => {
      this.updateProgressUI();
      this.checkQuartiles();
    });

    // Click on video to toggle play/pause
    this.videoElement.addEventListener('click', () => this.togglePlay());
  }

  private playVideo(): void {
    if (!this.videoElement) return;

    if (this.options.muteOnOpen !== false) {
      this.videoElement.muted = true;
      this.updateMuteButton(true);
      this.videoElement.play().catch(() => {});
    } else {
      this.videoElement.muted = false;
      const playPromise = this.videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          if (this.videoElement) {
            this.videoElement.muted = true;
            this.videoElement.play();
            this.updateMuteButton(true);
          }
        });
      }
    }
  }

  private pauseVideo(): void {
    if (!this.videoElement) return;
    this.videoElement.pause();
  }

  private updatePlayButton(isPlaying: boolean): void {
    const btn = this.container?.querySelector('.clickreel-play-btn');
    if (btn) {
      btn.innerHTML = isPlaying ? this.getPauseIcon() : this.getPlayIcon();
    }
  }

  private updateMuteButton(isMuted: boolean): void {
    const btn = this.container?.querySelector('.clickreel-mute-btn');
    if (btn) {
      btn.innerHTML = isMuted ? this.getMuteIcon() : this.getVolumeIcon();
    }
  }

  private updateProgressUI(): void {
    if (!this.videoElement || !this.container) return;

    // Batch DOM writes into a single rAF to prevent layout thrashing
    if (this.rafId) return; // Already scheduled
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (!this.videoElement) return;

      const { currentTime, duration } = this.videoElement;
      const percent = (currentTime / duration) * 100;

      // Update progress bar (cached reference)
      if (this.progressFillEl) {
        this.progressFillEl.style.width = `${percent}%`;
      }

      // Update time display (cached reference)
      if (this.timeDisplayEl) {
        this.timeDisplayEl.textContent = `${this.formatTime(currentTime)} / ${this.formatTime(duration)}`;
      }

      // Update story progress fill (cached reference)
      if (this.options.storyMode && this.storyFillEl) {
        this.storyFillEl.style.width = `${percent}%`;
      }
    });
  }

  private startProgressTracking(): void {
    // Quartile checks now happen in the timeupdate event handler
    // No interval needed
  }

  private stopProgressTracking(): void {
    // No interval to clear; quartile checks are event-driven
  }

  private checkQuartiles(): void {
    if (!this.videoElement) return;

    const { currentTime, duration } = this.videoElement;
    const percent = Math.floor((currentTime / duration) * 100);

    // Emit quartile events (25%, 50%, 75%)
    const quartiles = [25, 50, 75];
    for (const q of quartiles) {
      if (percent >= q && this.lastQuartile < q) {
        this.emitProgress(q as 25 | 50 | 75);
        this.lastQuartile = q;
      }
    }

    // Product card trigger
    if (this.productCardInstance && !this.productCardInstance.hasShown) {
      if (percent >= this.productCardInstance.triggerPercent) {
        this.productCardInstance.show();
      }
    }
  }

  private emitProgress(quartile: 0 | 25 | 50 | 75 | 100): void {
    if (!this.videoElement) return;

    const data: VideoProgressData = {
      videoId: this.options.video.id,
      currentTime: this.videoElement.currentTime,
      duration: this.videoElement.duration,
      percent: Math.floor((this.videoElement.currentTime / this.videoElement.duration) * 100),
      quartile,
    };

    this.options.onProgress?.(data);
  }

  private handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  // ===========================================================================
  // Private Methods - Stories, Social Proof
  // ===========================================================================

  private createStoryUI(wrapper: HTMLElement): void {
    const total = this.options.storyTotal ?? 1;
    const index = this.options.storyIndex ?? 0;

    // Story progress bar segments
    const storyBar = document.createElement('div');
    storyBar.className = 'clickreel-story-bar';

    for (let i = 0; i < total; i++) {
      const segment = document.createElement('div');
      segment.className = 'clickreel-story-segment';
      if (i < index) {
        segment.classList.add('completed');
      } else if (i === index) {
        segment.classList.add('active');
        const fill = document.createElement('div');
        fill.className = 'clickreel-story-fill';
        segment.appendChild(fill);
      }
      storyBar.appendChild(segment);
    }
    wrapper.appendChild(storyBar);

    // Story counter
    const counter = document.createElement('div');
    counter.className = 'clickreel-story-counter';
    counter.textContent = `${index + 1}/${total}`;
    wrapper.appendChild(counter);

    // Navigation zones
    const navLeft = document.createElement('div');
    navLeft.className = 'clickreel-story-nav clickreel-story-nav-left';
    navLeft.innerHTML = `<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg></div>`;
    navLeft.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onStoryAdvance?.('prev');
    });
    wrapper.appendChild(navLeft);

    const navRight = document.createElement('div');
    navRight.className = 'clickreel-story-nav clickreel-story-nav-right';
    navRight.innerHTML = `<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg></div>`;
    navRight.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onStoryAdvance?.('next');
    });
    wrapper.appendChild(navRight);
  }

  private createSocialProof(wrapper: HTMLElement): void {
    const config = this.options.socialProof;
    if (!config?.enabled || !config.text) return;

    const badge = document.createElement('div');
    const posClass = config.position || 'top-left';
    badge.className = `clickreel-social-proof ${posClass}`;
    badge.textContent = config.text;
    wrapper.appendChild(badge);
  }

  // ===========================================================================
  // Private Methods - Utilities
  // ===========================================================================

  private formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private getPlayIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
    </svg>`;
  }

  private getPauseIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>`;
  }

  private getVolumeIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>`;
  }

  private getMuteIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
    </svg>`;
  }

  private injectStyles(shadow: ShadowRoot): void {
    injectUtilityStyles(shadow);

    const existingStyle = shadow.querySelector('style[data-video-player]');
    if (existingStyle) return;

    const style = document.createElement('style');
    style.setAttribute('data-video-player', 'true');
    style.textContent = `
      .clickreel-video-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: clickreel-fade-in 0.2s ease;
        pointer-events: auto;
        z-index: 2;
      }

      .clickreel-video-overlay.closing {
        animation: clickreel-fade-out 0.2s ease forwards;
      }

      /* Desktop PiP Style */
      .clickreel-video-container.desktop {
        position: absolute;
        width: 380px;
        max-width: 90vw;
        background: #000;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        animation: clickreel-slide-up 0.3s ease;
      }

      /* Mobile Fullscreen */
      .clickreel-video-container.mobile {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        background: #000;
        display: flex;
        flex-direction: column;
      }

      .clickreel-close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 100;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        border: none;
        cursor: pointer;
        padding: 0;
      }

      .clickreel-close-btn:hover {
        background: rgba(0, 0, 0, 0.7);
      }

      /* Mobile: Larger touch target with contrast ring in safe area */
      .mobile .clickreel-close-btn {
        top: max(16px, env(safe-area-inset-top, 16px));
        right: max(16px, env(safe-area-inset-right, 16px));
        width: 44px;
        height: 44px;
        background: rgba(0, 0, 0, 0.6);
        border: 2px solid rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
      }

      .mobile .clickreel-close-btn:active {
        background: rgba(0, 0, 0, 0.8);
        border-color: rgba(255, 255, 255, 0.6);
      }

      .clickreel-video-wrapper {
        position: relative;
        width: 100%;
        background: #000;
      }

      .clickreel-video-wrapper[data-orientation="portrait"] {
        aspect-ratio: 9/16;
      }

      .clickreel-video-wrapper[data-orientation="landscape"] {
        aspect-ratio: 16/9;
      }

      .clickreel-video-wrapper[data-orientation="square"] {
        aspect-ratio: 1;
      }

      .mobile .clickreel-video-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        aspect-ratio: unset;
      }

      .clickreel-video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #000;
      }

      .clickreel-video-controls {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px 16px;
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
        display: flex;
        align-items: center;
        gap: 12px;
        opacity: 0;
        transition: opacity 0.2s;
      }

      .clickreel-video-wrapper:hover .clickreel-video-controls,
      .clickreel-video-controls:focus-within {
        opacity: 1;
      }

      .mobile .clickreel-video-controls {
        opacity: 1;
      }

      .clickreel-play-btn,
      .clickreel-mute-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
      }

      .clickreel-play-btn:hover,
      .clickreel-mute-btn:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      .clickreel-progress-container {
        flex: 1;
        cursor: pointer;
        padding: 8px 0;
      }

      .clickreel-progress-bar {
        height: 4px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
        overflow: hidden;
      }

      .clickreel-progress-fill {
        height: 100%;
        background: white;
        width: 0;
        transition: width 0.1s;
      }

      .clickreel-time-display {
        color: white;
        font-size: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        white-space: nowrap;
      }

      .clickreel-cta-btn {
        display: block;
        padding: 14px 24px;
        margin: 16px;
        border-radius: 8px;
        color: white;
        text-align: center;
        text-decoration: none;
        font-weight: 600;
        font-size: 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: transform 0.2s, filter 0.2s;
      }

      .clickreel-cta-btn:hover {
        transform: translateY(-2px);
        filter: brightness(1.1);
      }

      .mobile .clickreel-cta-btn {
        margin: 0 16px 24px;
      }

      .clickreel-player-watermark {
        position: absolute;
        bottom: 6px;
        left: 0;
        right: 0;
        text-align: center;
        font-size: 10px;
        color: rgba(255, 255, 255, 0.5);
        text-decoration: none;
        pointer-events: auto;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: color 0.2s ease;
        z-index: 10;
      }

      .clickreel-player-watermark:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    `;
    shadow.appendChild(style);
  }
}
